import {
  Alert,
  Badge,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';

import { CatalogueErrorAlert } from './CatalogueErrorAlert';
import { describeSharedPrice, NOTES_MAX_LENGTH, type SharedProduct } from './orderDraft';
import type { OrderSharedDocuments } from './useOrderSharedDocuments';

/** Step 2 when ordering for every company - the documents all of their
 * countries offer. */
export function SharedDocumentsStep({ documents }: Readonly<{ documents: OrderSharedDocuments }>) {
  const { catalogue, offered, omitted } = documents;
  return (
    <Stack gap="sm" mt="sm">
      {catalogue.isLoading && (
        <Group>
          <Loader size="sm" />
          <Text size="sm">Loading the catalogues…</Text>
        </Group>
      )}
      {catalogue.error && !catalogue.isLoading && (
        <CatalogueErrorAlert error={catalogue.error} onRetry={catalogue.reload} />
      )}
      {catalogue.loaded && offered.length === 0 && (
        <Alert color="gray" title="Nothing offered everywhere">
          No document is offered in all of these countries - order for one company at a time.
        </Alert>
      )}
      {offered.length > 0 && (
        <Stack gap={6}>
          {offered.map((product) => (
            <SharedProductRow
              key={product.code}
              product={product}
              checked={documents.selected.includes(product.code)}
              onToggle={(ticked) =>
                documents.setSelected(
                  ticked
                    ? [...documents.selected, product.code]
                    : documents.selected.filter((entry) => entry !== product.code)
                )
              }
            />
          ))}
        </Stack>
      )}
      {omitted.length > 0 && (
        <Text size="xs" c="dimmed" data-testid="registry-order-omitted">
          Left out:{' '}
          {omitted
            .map((entry) => `${entry.code} is not offered for ${entry.missingIn.join(', ')}`)
            .join('; ')}
          .
        </Text>
      )}
      <Textarea
        label="Notes to Armadillo"
        description="Optional - goes with every order"
        value={documents.notes}
        onChange={(event) => documents.setNotes(event.currentTarget.value)}
        maxLength={NOTES_MAX_LENGTH}
        autosize
        minRows={2}
        data-testid="registry-order-notes"
      />
    </Stack>
  );
}

function SharedProductRow({
  product,
  checked,
  onToggle,
}: Readonly<{ product: SharedProduct; checked: boolean; onToggle: (ticked: boolean) => void }>) {
  const countries = Object.keys(product.perCountry).length;
  const price = describeSharedPrice(product);
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      data-testid={`registry-product-${product.code}`}
    >
      <Checkbox
        checked={checked}
        onChange={(event) => onToggle(event.currentTarget.checked)}
        label={
          <Stack gap={0}>
            <Text size="sm">{product.description ?? product.code}</Text>
            <Text size="xs" c="dimmed">
              {product.code}
              {countries > 1 ? ` · ${countries} countries` : ''}
            </Text>
          </Stack>
        }
      />
      <Group gap={6} style={{ flexShrink: 0 }}>
        {product.anyQuote && price !== 'Quote' && (
          <Tooltip label="At least one country prices this on quote: that order gets a quote email first, not a document">
            <Badge size="xs" color="orange" variant="light">
              quote
            </Badge>
          </Tooltip>
        )}
        {price === 'Quote' ? (
          // Every country prices this on quote: the one word says so.
          <Tooltip label="Priced on quote in every country: each order gets a quote email first, not a document">
            <Badge size="sm" color="orange" variant="light" radius="sm">
              {price}
            </Badge>
          </Tooltip>
        ) : (
          price && (
            <Text size="sm" fw={600}>
              {price}
            </Text>
          )
        )}
      </Group>
    </Group>
  );
}
